import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, combineLatest, of } from 'rxjs';
import { map, switchMap, shareReplay, tap } from 'rxjs/operators';
import {
  IVehicleSpec,
  VehicleSpecsService,
} from '@Services/vehicle-specs.service';
import {
  IVehicle,
  VehiclesOnsiteService,
} from '@Services/vehicles-onsite.service';
import { mapFrom } from './helpers';
import { XlatPipe } from '@amt/i18n/xlat-pipe';
import OcConfigSvc from '@Services/ocConfigSvc';
import { APILoadingService } from '@Services/api-loading-functions.service';
import { IFullVisualInspections, IVisualInspections } from '@Interfaces/visualInspection.interfaces';
import { IPosition, IVisualInspectionRawData } from '@Models/condition-monitoring/IVisualInspection';

export enum AlertTypes {
  MatchingLimitExceeded = 'MATCHINGLIMITEXCEEDED',
  AsymmetricWearLimitExceeded = 'ASYMMETRICALWEAR',
}

export interface IDisplayVehicle {
  serialNo: string;
  altText: string;
  application: string;
  model: string;
  equipmentId: guid;
  issues: MonitoringIssueData[];
  rawIssues: MonitoringIssueData[];
  spec: IVehicleSpec;
  sizes: IAxleSpecSize[];
}

export interface IAxleSpecSize {
  tyreWidth: number;
  tyreDiameter: number;
  rimDiameter: number;
}

export interface ISerialNumberDetails {
  prefix: string;
  manufacturer: string;
  site: string;
  formatted: string;
  hierarchical: string;
}

export interface IReadingDetails {
  date: Date;
  innerOuterValues: string;
  type: string; //Check return type from http call
  unit: string;
  value: number; //Check return type from http call
}

export interface IPositionData {
  component: ISelectedPosition;
  position: string;
}

//Interface for selected position returned from ocVehicleSchematic
export interface ISelectedPosition {
  type: string;
  serialNumberDetails: ISerialNumberDetails;
  equipmentId: guid;
  siteId: guid;
  hoursSinceLastNDT: string; //Check return type from http call
  totalDistance: string; //Check return type from http call
  totalHours: IReadingDetails;
  remainingDepth: IReadingDetails;
  primaryOTD: IReadingDetails;
  lifeOTD: IReadingDetails;
  specification: string;
  equipmentLifeId: guid;
  typeId: guid;
  pressureSensorId: guid;
  positionId: guid;
  serialNumber: string;
  isDoubleGutter: boolean;
  percentWorn: string; //Calculated by code somewhere, somehow...
  fittedVehicle: string; //Added in when position is loaded into component
  positionLabel: string; //Added back in component
}

//interface from API
export interface IVisualInspectionBasicApi {
  action: string;
  cause: string;
  inspectionDate: Date;
  equipmentId: guid;
  posLabel: string;
  lastActionChangeDate?: Date;
  daysToResolve?: number;
  inspectionFrequencyDays?: number;
}

export interface IIssueText{
  issue: number;
  issueText: string;
  posIdx: number;
}

export interface MonitoringIssueData extends IVisualInspectionBasicApi, IIssueText {
  nextInspection?: Date;
  InspectionDue?: Date;
}

export interface ICMTreadWear {
  originalDepth?: string,
}

export interface IAsymetricalWear extends ICMTreadWear {
  depth1?: string;
  depth2?: string;
  wearValue?: string;
  escalationLevel?: number,
}

export interface IWornOutTyre extends ICMTreadWear {
  averageRemainingDepth?: string,
  wearValue?: string,
}

export interface ICMEquipmentAlerts extends MonitoringIssueData, IAsymetricalWear, IWornOutTyre {
}

export interface ICMTreadWearDetailsDisplay extends ICMEquipmentAlerts {
  date: Date,
  depth: string,
  causeLabel: string,
  // asywear
  wearDifference: number,
  severity: number,
  // worn out
  wearPercent: number,
  wearPercentLabel: string,
}

export const CONTINUE_TO_MONITOR_RES = 'Continue to Monitor';

@Injectable({
  providedIn: 'root',
})
export class ConditionMonitoringService {
  constructor(
    @Inject('WindowFactory') private WindowFactory: any,
    private http: HttpClient,
    private vehicleSvc: VehiclesOnsiteService,
    private vehicleSpecsSvc: VehicleSpecsService,
    private xlatPipe: XlatPipe,
    private ocConfigSvc: OcConfigSvc,
    private _apiLoadingService: APILoadingService,
  ) {}

  public static getArrPositions(vehicle: any) {
    const axles = vehicle.specification.axles;
    const arrPositions: IPosition[] = [];
    for (let i = 0; i < axles.length; i++) {
      const positions = axles[i].positions;
      for (let j = 0; j < positions.length; j++) {
        const fitments = positions[j].fitments;
        for (let k = 0; k < fitments.length; k++) {
          if (fitments[k].type === 'Tyre')
            arrPositions.push({
              id: fitments[k].positionId,
              axleId: axles[i].id,
              position: positions[j].label,
              equipmentId: fitments[k].equipmentId,
              siteId: fitments[k].siteId,
              serialNumber: fitments[k].serialNumber,
            });
        }
      }
    }
    return arrPositions;
  }

  private currentVehicle$: Observable<any> = null;
  public getVehicle(eId: string): Observable<any> {
    if (!this.currentVehicle$) {
      this.currentVehicle$ = this.getVehicleDetails(eId).pipe(
        tap((result) => {
          // concise vi record
          result?.visualInspectionDetails?.forEach((viDetail) => {
            viDetail?.visualInspectionItems.forEach((viItem) => {
              const records = viItem.visualInspectionRecords;
              if (records?.length === 1) {
                viItem.visualInspectionRecord = records[0]
              } else if (records?.length > 1) {
                const concisedRecord = { ...records[0], lastUpdatedDate: records[0].date }
                for(let i = 1; i < records?.length; i += 1) {
                  concisedRecord.lastUpdatedDate = records[i].date;
                  concisedRecord.inspectedBy = records[i].inspectedBy
                  if (concisedRecord.actionId !== records[i].actionId) {
                    concisedRecord.id = records[i].id;
                    concisedRecord.date = records[i].date;
                    concisedRecord.actionId = records[i].actionId;
                    concisedRecord.action = records[i].action;
                    concisedRecord.actionDaysToResolve = records[i].actionDaysToResolve;
                    concisedRecord.inspectionFrequencyDays = records[i].inspectionFrequencyDays;
                    concisedRecord.comment = records[i].comment;
                  }
                }
                viItem.visualInspectionRecord = concisedRecord;
              }
            });
          });
        }),
        shareReplay(1),
      );
    }

    return this.currentVehicle$;
  }

  private vehicles$ = this.vehicleSvc.vehicles$;
  private vehicleSpecs$ = this.vehicleSpecsSvc.vehicleSpecs$;

  private visInspMonRequest$ = this.http
    .get<Record<string, IVisualInspectionBasicApi[]>>(
      baseApiUrl + 'equipment/vehicle/conditionMonitoringData'
    )
    .pipe(
      map((w) => Array.from(Object.entries(w)))
      //shareReplay(1), //removed so we hit the server each time
    );

  private cmEquipmentAlerts$ = this.http
    .get<Record<string, ICMEquipmentAlerts[]>>(
      baseApiUrl + 'equipment/vehicle/cmEquipmentAlerts'
    )
    .pipe(
      map((s) => Array.from(Object.entries(s))),
      //map(s => mapFrom(s)),
      //shareReplay(1),
    );

  private diagramSizeRequest$ = this.http
    .get<Record<string, IAxleSpecSize[]>>(
      baseApiUrl + 'equipment/vehicle/paramsForDiagram'
    )
    .pipe(
      map((s) => mapFrom(s))
      //shareReplay(1), //removed so we hit the server each time
    );

  //Pass a 'new' date into this so we don't corrupt the original date (passed by reference)
  private addDays(date: Date, days: number): Date {
    date.setDate(date.getDate() + days);
    return date;
  }

  //private vehiclesWithAlertData$ = combineLatest(this.vehicles$, this.vehicleSpecs$, this.cmEquipmentAlerts$, this.diagramSizeRequest$).pipe(
  //    map(w => Array.from(w[0].entries()).map(v => {
  //        let [vehicleId, vehicle] = v;
  //        let [vehicleMap, vehSpecMap, alertMap, diagramSizes] = w;
  //        let alerts = alertMap.get(vehicle.serialNo.toLowerCase()) || [];
  //        if (alerts.length > 0)
  //            alerts.sort((a, b) => parseInt(a.pos) - parseInt(b.pos));
  //        let spec = vehSpecMap.get(vehicle.specificationId);
  //        let sizes = diagramSizes.get(vehicle.specificationId);
  //        let alertText = alerts.map(m => `Pos ${m.pos}: ${m.description}`).join('<br />') || this.xlatPipe.transform('conditionmonitoring.NoIssue');
  //        return <IDisplayVehicle>{
  //            serialNo: vehicle.serialNo,
  //            altText: this.xlatPipe.transform('conditionmonitoring.Alerts') + ':<br />' + alertText,
  //            application: spec?.vehicleApplication || this.xlatPipe.transform('conditionmonitoring.Unknown'),
  //            model: spec?.model || this.xlatPipe.transform('conditionmonitoring.Unknown'),
  //            equipmentId: vehicle.id,
  //            issues: [], //alerts,
  //            spec: spec,
  //            sizes: sizes
  //        };
  //    }))
  //);

  //public get vehiclesData$() {
  //    return this.vehiclesWithIssueData$;
  //}

  //public get alertData$() {
  //    return this.vehiclesWithAlertData$;
  //}

  //public getIssuesForVehicle$(vehicle$: Observable<IVehicle>) {
  //    return combineLatest(vehicle$, this.visInspMonData$).pipe(
  //        switchMap(i => of(i[0] && i[1].get(i[0]?.id)))
  //    );
  //}

  //public getAlertsBySerial$(serial: string): Observable<ICMEquipmentAlerts[]> {
  //    return this.cmEquipmentAlerts$.pipe(
  //        map(x => x.get(serial.toLowerCase()))
  //    );
  //}

  getDiagramSizes$(vehicle$: Observable<IVehicle>) {
    return combineLatest(vehicle$, this.diagramSizeRequest$).pipe(
      switchMap((i) => of(i[0] && i[1].get(i[0]?.specificationId)))
    );
  }

  public getVehicleDetails(id: guid) {
    return this.http
      .post<any>(
        baseApiUrl + 'equipment/vehicle/ConditionMonitoringVehicleDetails',
        { equipmentId: id }
      )
      .pipe
      //tap(x => console.log(x))
      ();
  }
  //Returns VI items sorted by date desc
  public getItems(selectedComponent: ISelectedPosition, currentVehicle: any) {
    let items;

    if (selectedComponent === null) {
      items = currentVehicle.visualInspectionDetails
        .flatMap((a) => a.visualInspectionItems)
        .sort(
          (a, b) =>
            b.visualInspectionRecord.date.getTime() -
            a.visualInspectionRecord.date.getTime()
        );
    } else {
      items = currentVehicle.visualInspectionDetails
        .filter((obj) => {
          if (obj.equipmentId === selectedComponent.equipmentId) {
            return true;
          }
          return false;
        })
        .flatMap((a) => a.visualInspectionItems)
        .sort(
          (a, b) =>
            b.visualInspectionRecord.date.getTime() -
            a.visualInspectionRecord.date.getTime()
        );
    }
    return items;
  }

  public getActiveItems(
    selectedComponent: ISelectedPosition,
    currentVehicle: any
  ) {
    let items;
    if (selectedComponent === null) {
      items = currentVehicle.visualInspectionDetails
        .flatMap((a) => a.visualInspectionItems)
        .filter(
          (x) =>
            x.visualInspectionRecord.action !==
              'VisualInspectionAction.Continue to Monitor' &&
            x.visualInspectionRecord.action !==
              'VisualInspectionAction.Schedule for Removal in Next Service' &&
            x.active
        )
        .sort(
          (a, b) =>
            this.getActionDueDate(
              b.visualInspectionRecord.date.getTime(),
              b.visualInspectionRecord.actionDaysToResolve
            ) -
            this.getActionDueDate(
              a.visualInspectionRecord.date.getTime(),
              a.visualInspectionRecord.actionDaysToResolve
            )
        );
    } else {
      const selectedItem = currentVehicle.visualInspectionDetails.find(
        (obj) => obj.equipmentId === selectedComponent.equipmentId
      );
      if (selectedItem)
        items = selectedItem
          ? selectedItem.visualInspectionItems
              .filter(
                (x) =>
                  x.active &&
                  x.visualInspectionRecord.action !==
                    'VisualInspectionAction.Continue to Monitor' &&
                  x.visualInspectionRecord.action !==
                    'VisualInspectionAction.Schedule for Removal in Next Service'
              )
              .sort(
                (a, b) =>
                  this.getActionDueDate(
                    b.visualInspectionRecord.date.getTime(),
                    b.visualInspectionRecord.actionDaysToResolve
                  ) -
                  this.getActionDueDate(
                    a.visualInspectionRecord.date.getTime(),
                    a.visualInspectionRecord.actionDaysToResolve
                  )
              )
          : [];
    }
    return items;
  }

  public getActiveItemsWithActiveStatusOnly(
    selectedComponent: ISelectedPosition,
    currentVehicle: any
  ) {
    let items;
    if (selectedComponent === null) {
      items = currentVehicle.visualInspectionDetails
        .flatMap((a) => a.visualInspectionItems)
        .filter((x) => x.active)
        .sort(
          (a, b) =>
            this.getActionDueDate(
              b.visualInspectionRecord.date.getTime(),
              b.visualInspectionRecord.actionDaysToResolve
            ) -
            this.getActionDueDate(
              a.visualInspectionRecord.date.getTime(),
              a.visualInspectionRecord.actionDaysToResolve
            )
        );
    } else {
      const selectedItem = currentVehicle.visualInspectionDetails.find(
        (obj) => obj.equipmentId === selectedComponent.equipmentId
      );
      if (selectedItem)
        items = selectedItem.visualInspectionItems
          .filter((x) => x.active)
          .sort(
            (a, b) =>
              this.getActionDueDate(
                b.visualInspectionRecord.date.getTime(),
                b.visualInspectionRecord.actionDaysToResolve
              ) -
              this.getActionDueDate(
                a.visualInspectionRecord.date.getTime(),
                a.visualInspectionRecord.actionDaysToResolve
              )
          );
    }
    return items;
  }

  public getLatestActiveItemsWithActiveStatusOnly(
    selectedComponent: ISelectedPosition,
    currentVehicle: any
  ) {
    let items = [];
    if (!!selectedComponent) {
      items = currentVehicle.visualInspectionDetails
        .flatMap((a) => a.visualInspectionItems)
        .filter((x) => x.active)
        .sort(
          (a, b) =>
            b.visualInspectionRecord.date.getTime() -
            a.visualInspectionRecord.date.getTime()
        );
    } else {
      const selectedItem = currentVehicle.visualInspectionDetails.find(
        (obj) => obj.equipmentId === selectedComponent.equipmentId
      );
      if (!!selectedItem)
        items = selectedItem.visualInspectionItems
          .filter((x) => x.active)
          .sort(
            (a, b) =>
              b.visualInspectionRecord.date.getTime() -
              a.visualInspectionRecord.date.getTime()
          );
    }
    return items;
  }

  public getActionDueDate(date: Date, actionDaysToResolve: number) {
    return new Date(date).setDate(
      new Date(date).getDate() + actionDaysToResolve
    );
  }
  public getActionDueDateSupporter(date: Date, item: any) {
    const cause = item.visualInspectionRecord.action;
    if (
      cause === this.xlatPipe.transform('conditionmonitoring.ContMonitor') ||
      cause ===
        this.xlatPipe.transform('VisualInspectionAction.scheduled_next_service')
    )
      return null;

    return new Date(date).setDate(
      new Date(date).getDate() + item.visualInspectionRecord.actionDaysToResolve
    );
  }

  public formatDate(objData) {
    if (!objData) return;
    const day = String(objData.getDate()).padStart(2, '0');
    const month = String(objData.getMonth() + 1).padStart(2, '0'); // Month is 0-indexed
    const year = objData.getFullYear();
    const minutes = String(objData.getMinutes()).padStart(2, '0');
    const hours = String(objData.getHours()).padStart(2, '0');
    const formattedDate = `${day}-${month}-${year} ${hours}:${minutes}`;
    return formattedDate;
  }
  public viCount(
    activeOnly: boolean,
    selectedComponent: ISelectedPosition,
    currentVehicle: any
  ) {
    let items;

    if (selectedComponent === null) {
      items = currentVehicle.visualInspectionDetails;
    } else {
      items = currentVehicle.visualInspectionDetails.filter((obj) => {
        if (obj.equipmentId === selectedComponent.equipmentId) return true;

        return false;
      });
    }

    if (activeOnly)
      return items.reduce(
        (count, current) =>
          count +
          current.visualInspectionItems.filter((obj) => {
            if (obj.active) {
              return true;
            }

            return false;
          }).length,
        0
      );

    return items.reduce(
      (count, current) => count + current.visualInspectionItems.length,
      0
    );
  }

  public get VehicleSpecs$() {
    return this.vehicleSpecs$;
  }

  private vehiclesWithSpecs$ = combineLatest([
    this.vehicles$,
    this.vehicleSpecs$,
    this.diagramSizeRequest$
  ]).pipe(
    map(([vehicleMap, vehSpecMap, diagramSizes]) =>
      Array.from(vehicleMap.entries()).map(([vehicleId, vehicle]) => {
        const spec = vehSpecMap.get(vehicle.specificationId);
        const sizes = diagramSizes.get(vehicle.specificationId);
        const issueText = this.xlatPipe.transform('conditionmonitoring.NoIssue');
        return <IDisplayVehicle>{
          serialNo: vehicle.serialNo,
          altText: `${this.xlatPipe.transform('conditionmonitoring.VisualInspections')}:<br />${issueText}`,
          application:
            spec?.vehicleApplication ||
            this.xlatPipe.transform('conditionmonitoring.Unknown'),
          model:
            spec?.model ||
            this.xlatPipe.transform('conditionmonitoring.Unknown'),
          equipmentId: vehicle.id,
          issues: [],
          spec: spec,
          sizes: sizes,
        };
      })
    )
  );

  public showComponent(data: any, showVi: boolean = true) {
    this.WindowFactory.openItem({
      component: 'component-details',
      caption: this.xlatPipe.transform(
        'equipment.open' + 'Tyre',
        data.serialNumber
      ),
      windowRelatedRecordId: data.equipmentId,
      initParams: {
        equipmentId: data.equipmentId,
        siteId: data.siteId,
        componentType: 'Tyre',
        serialNumber: data.serialNumber,
        showCloseOnSave: true,
        showVi: showVi,
      },
      width: 800,
      height: 850,
    });
  }

  public get getVehicles$() {
    return this.vehiclesWithSpecs$;
  }

  public get visualInspections$() {
    return this.visInspMonRequest$;
  }

  public get currentAlerts$() {
    return this.cmEquipmentAlerts$;
  }

  public getVisualInspectionRecords(equipmentId: string, isVehicle = true): Observable<IVisualInspectionRawData[]> {
    return isVehicle
      ? this._apiLoadingService
        .genericPost('equipment/vehicle/getFieldSurveyVisualInspection', {
          vehicles: [
            {
              equipmentId: equipmentId,
              date: null,
            },
          ],
        }).pipe(
          map((responses: IFullVisualInspections[]) => responses?.map((item, index) => ({
              ...item,
              index,
            })).filter((item) => !!item?.visualInspections)),
        )
      : this._apiLoadingService
        .genericPost('equipment/component/getVisualInspections', {
          filterValues: {
            equipmentId: equipmentId,
          },
          page: 1,
          pageSize: 10,
        }).pipe(
          map(({ result }: IVisualInspections) => [({
            positionId: equipmentId,
            visualInspections: result,
          })]),
        );
  }

  showVehicle(vehicle: IDisplayVehicle, currentVehicle: any) {
    this.WindowFactory.openItem({
      component: 'vehicle-details',
      caption: this.xlatPipe.transform(
        'equipment.openVehicle',
        vehicle.serialNo
      ),
      windowRelatedRecordId: currentVehicle.id,
      initParams: {
        equipmentId: currentVehicle.id,
        siteId: this.ocConfigSvc.user.site.id,
        clientId: this.ocConfigSvc.user.site.id,
        serialNumber: vehicle.serialNo,
        showCloseOnSave: false,
      },
      width: 800,
      height: 850,
      modal: false,
    });
    }

  getAlertIssue(alert: ICMEquipmentAlerts, config: string[]) {
    const severityTreshold = 5; // < 5mm is low severity, >= 5 is high severity
    switch (alert.cause) {
      case 'low_tread':
        return config.findIndex(issue => issue === 'tw_low_tread_tyre_note');
      case 'worn_out':
        return config.findIndex(issue => issue === 'tw_worn_out_tyre_note');
      case "ASYMMETRICALWEAR":
        const severity = Math.abs(parseInt(alert?.depth1 || '0') - parseInt(alert?.depth2 || '0')) < parseInt(alert?.wearValue || '0') + severityTreshold ? 0 : 1;
        return config.findIndex(issue => issue === 'LowAsymmetricWear') + severity;
      case "MATCHINGLIMITEXCEEDED":
      default:
        return config.findIndex(issue => issue === 'tw_tread_matching_issue');
    }
  }

  assignAlertIssue(alert: ICMEquipmentAlerts, config: string[]) {
    alert.issue = this.getAlertIssue(alert, config);
  }

    openMaintenanceSession(vehicle: IDisplayVehicle) {
      this.WindowFactory.openItem({
          component: 'maintenance',
          headerOff: false,
          footerOff: true,
          caption: this.xlatPipe.transform('equipment.maintenanceSession'),
          initParams: {
              vehicleId: vehicle.equipmentId,
              display: vehicle.serialNo + ' - ' + vehicle.spec,
          },
          height: 700 + this.WindowFactory.headerHeight,
          width: 1100,
          modal: false,
      });
  }
}
